import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { authApi } from "@/api/auth";
import { authKeys, sessionQueryOptions } from "@/auth/session";
import { useAuthStore } from "@/auth/store";
import type {
  LoginCredentials,
  MfaChallengeResponse,
  User,
} from "@/types/auth";

// Re-export for backward compatibility with existing consumers
export { authKeys } from "@/auth/session";

/**
 * Reads the current session from TanStack Query cache.
 *
 * The cache is always warm inside authenticated routes because
 * authenticatedRoute.beforeLoad calls ensureQueryData before
 * any component renders. This hook simply subscribes to that
 * cached data — no side effects, no auth state mutations.
 */
export function useSession() {
  return useQuery(sessionQueryOptions());
}

export function useUser(): User | null {
  const { data: user } = useSession();
  return user ?? null;
}

function isMfaChallenge(
  response: User | MfaChallengeResponse,
): response is MfaChallengeResponse {
  return "mfaRequired" in response && response.mfaRequired;
}

export function useLogin() {
  const queryClient = useQueryClient();
  const { setAuthenticated, setMfaPending } = useAuthStore();

  return useMutation({
    mutationFn: (credentials: LoginCredentials) => authApi.login(credentials),
    onSuccess: (response) => {
      if (isMfaChallenge(response)) {
        setMfaPending({
          mfaToken: response.mfaToken,
          expiresAtUtc: response.expiresAtUtc,
          destination: response.destination ?? null,
          resendAvailableAtUtc: response.resendAvailableAtUtc ?? null,
        });
        return;
      }

      setAuthenticated();
      queryClient.setQueryData(authKeys.me(), response);

      // Fetch CSRF token after login for subsequent mutating requests
      authApi.csrf().catch(() => {
        // CSRF fetch is best-effort, don't block login flow
      });
    },
  });
}

export function useMfaVerify() {
  const queryClient = useQueryClient();
  const { setAuthenticated } = useAuthStore();

  return useMutation({
    mutationFn: ({
      mfaToken,
      code,
      rememberDevice,
    }: {
      mfaToken: string;
      code: string;
      rememberDevice: boolean;
    }) => authApi.mfaVerify(mfaToken, code, rememberDevice),
    onSuccess: (user) => {
      setAuthenticated();
      queryClient.setQueryData(authKeys.me(), user);

      // CSRF fetch is best-effort, don't block MFA flow
      authApi.csrf().catch(() => {
        /* intentionally empty */
      });
    },
  });
}

export function useMfaResend() {
  const { updateMfaPending } = useAuthStore();

  return useMutation({
    mutationFn: (mfaToken: string) => authApi.mfaResend(mfaToken),
    onSuccess: (response) => {
      updateMfaPending({
        expiresAtUtc: response.expiresAtUtc,
        resendAvailableAtUtc: response.resendAvailableAtUtc,
      });
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { clearAuth } = useAuthStore();

  return useMutation({
    mutationFn: () => authApi.logout(),
    onSuccess: () => {
      clearAuth();
      queryClient.clear();
      void navigate({ to: "/login" });
    },
  });
}
